'use client'

import * as React from 'react'
import { Plus, Search, Filter } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { cn } from 'lib/utils'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../firebase'

function getRiskColor(score) {
  if (score >= 15) return { bg: 'bg-destructive', text: 'text-destructive', light: 'bg-destructive/15 text-destructive', label: 'Critical' }
  if (score >= 8) return { bg: 'bg-warning', text: 'text-warning', light: 'bg-warning/15 text-warning', label: 'High' }
  if (score >= 4) return { bg: 'bg-secondary', text: 'text-secondary-foreground', light: 'bg-secondary text-secondary-foreground', label: 'Medium' }
  return { bg: 'bg-success', text: 'text-success', light: 'bg-success/15 text-success', label: 'Low' }
}

export default function RisksPage() {
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [risks, setRisks] = React.useState([])
  const [projects, setProjects] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadRisks() {
      try {
        const [riskSnap, projectSnap] = await Promise.all([
          getDocs(collection(db, 'risks')),
          getDocs(collection(db, 'projects')),
        ])
        if (!isMounted) return
        setRisks(riskSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
        setProjects(projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })))
      } catch {
        if (!isMounted) return
        setRisks([])
        setProjects([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadRisks()

    return () => {
      isMounted = false
    }
  }, [])

  const projectMap = projects.reduce((acc, project) => {
    acc[project.id] = project
    return acc
  }, {})

  const normalizedRisks = risks.map((risk) => {
    const likelihood = Number(risk.likelihood || 0)
    const impact = Number(risk.impact || 0)
    const riskScore = Number(risk.riskScore ?? (likelihood && impact ? likelihood * impact : 0))
    const status = String(risk.status || 'open').toLowerCase()
    const owner = risk.ownerName || risk.owner || risk.ownerDisplayName || risk.ownerId || 'Unassigned'
    return {
      ...risk,
      likelihood,
      impact,
      riskScore,
      status,
      owner,
      category: risk.category || 'Uncategorized',
    }
  })

  const filtered = normalizedRisks.filter((risk) => {
    const project = projectMap[risk.projectId]
    const projectName = `${project?.code || ''} ${project?.title || ''}`.toLowerCase()
    const matchesSearch =
      String(risk.description || '').toLowerCase().includes(search.toLowerCase()) ||
      String(risk.category || '').toLowerCase().includes(search.toLowerCase()) ||
      String(risk.owner || '').toLowerCase().includes(search.toLowerCase()) ||
      projectName.includes(search.toLowerCase())
    const matchesStatus = statusFilter === 'all' || risk.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const totalRisks = normalizedRisks.length
  const openRisks = normalizedRisks.filter((risk) => risk.status === 'open').length
  const mitigatedRisks = normalizedRisks.filter((risk) => risk.status === 'mitigated').length
  const criticalRisks = normalizedRisks.filter((risk) => risk.riskScore >= 15).length
  const highRisks = normalizedRisks.filter((risk) => risk.riskScore >= 8).length
  const lowMediumRisks = Math.max(totalRisks - highRisks, 0)

  const riskTypeData = [
    { name: 'High Risk', value: highRisks, color: 'oklch(0.577 0.245 27.325)' },
    { name: 'Low/Medium', value: lowMediumRisks, color: 'oklch(0.55 0.16 150)' },
  ]

  const severityData = [1, 2, 3, 4, 5].map((impact) => ({
    name: String(impact),
    count: normalizedRisks.filter((risk) => risk.impact === impact).length,
  }))

  const likelihoodData = [1, 2, 3, 4, 5].map((likelihood) => ({
    name: String(likelihood),
    count: normalizedRisks.filter((risk) => risk.likelihood === likelihood).length,
  }))

  const ownerCounts = normalizedRisks.reduce((acc, risk) => {
    acc[risk.owner] = (acc[risk.owner] || 0) + 1
    return acc
  }, {})
  const responsibleData = Object.entries(ownerCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6)

  const categoryCounts = normalizedRisks.reduce((acc, risk) => {
    const key = risk.category || 'Uncategorized'
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})
  const categoryPalette = [
    'oklch(0.35 0.12 145)',
    'oklch(0.87 0.15 85)',
    'oklch(0.55 0.16 150)',
    'oklch(0.7 0.17 65)',
    'oklch(0.65 0.15 210)',
  ]
  const categoryData = Object.entries(categoryCounts).map(([name, value], index) => ({
    name,
    value,
    color: categoryPalette[index % categoryPalette.length],
  }))

  const totalContingency = normalizedRisks.reduce((sum, risk) => sum + Number(risk.contingencyAmount || risk.contingency || 0), 0)

  // Risk level percentages
  const lowCount = normalizedRisks.filter((risk) => risk.riskScore < 4).length
  const medCount = normalizedRisks.filter((risk) => risk.riskScore >= 4 && risk.riskScore < 15).length
  const highCount = normalizedRisks.filter((risk) => risk.riskScore >= 15).length
  const lowPct = totalRisks ? Math.round((lowCount / totalRisks) * 100) : 0
  const medPct = totalRisks ? Math.round((medCount / totalRisks) * 100) : 0
  const highPct = totalRisks ? Math.round((highCount / totalRisks) * 100) : 0

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header with Tab Navigation - Inspired by Risk Management Dashboard */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
              Risk Management Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Risk identification, assessment, analysis, and mitigation tracking
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="bg-muted">
          <TabsTrigger value="dashboard" className="text-xs">Dashboard</TabsTrigger>
          <TabsTrigger value="register" className="text-xs">Risk Register</TabsTrigger>
          <TabsTrigger value="matrix" className="text-xs">Risk Matrix</TabsTrigger>
        </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard" className="mt-4 flex flex-col gap-6">
          {/* Top Stats Row - Inspired by Risk Mgmt reference */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="border border-border relative overflow-hidden">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total Risks</p>
                <p className="text-3xl font-bold text-foreground mt-1">{loading ? '—' : totalRisks}</p>
                <p className="text-xs text-muted-foreground">Identified across portfolio</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
              </CardContent>
            </Card>
            <Card className="border border-border relative overflow-hidden">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-destructive font-medium">Open Risks</p>
                <p className="text-3xl font-bold text-destructive mt-1">{loading ? '—' : openRisks}</p>
                <p className="text-xs text-muted-foreground">Requiring action</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-destructive" />
              </CardContent>
            </Card>
            <Card className="border border-border relative overflow-hidden">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Mitigated</p>
                <p className="text-3xl font-bold text-success mt-1">{loading ? '—' : mitigatedRisks}</p>
                <p className="text-xs text-muted-foreground">Successfully addressed</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-success" />
              </CardContent>
            </Card>
            <Card className="border border-border relative overflow-hidden">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Contingency Value</p>
                <p className="text-3xl font-bold text-foreground mt-1">
                  {loading ? '—' : totalContingency.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Reserved budget (if provided)</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-secondary" />
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 1: Responsible Person, Risk Type Donut, Category Donut, Risk Level Bars */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {/* Responsible Person - Horizontal Bar */}
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">Responsible Person</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={responsibleData} layout="vertical" barSize={14}>
                    <XAxis type="number" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" width={80} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Bar dataKey="count" fill="oklch(0.35 0.12 145)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Risk Type Donut */}
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">Risk Type</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={riskTypeData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" paddingAngle={4} strokeWidth={0}>
                      {riskTypeData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4">
                  {riskTypeData.map((entry) => (
                    <div key={entry.name} className="flex items-center gap-1.5">
                      <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="text-[10px] text-muted-foreground">{entry.name} ({entry.value})</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Risk by Category Donut */}
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">By Category</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={categoryData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" paddingAngle={4} strokeWidth={0}>
                      {categoryData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {categoryData.map((entry) => (
                    <div key={entry.name} className="flex items-center gap-1.5">
                      <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="text-[10px] text-muted-foreground">{entry.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Risk Level Horizontal Bars */}
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">Risk Level</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3">
                  {[
                    { label: 'High', count: criticalRisks, color: 'bg-destructive', textColor: 'text-destructive' },
                    { label: 'Medium', count: medCount, color: 'bg-warning', textColor: 'text-warning' },
                    { label: 'Low', count: lowCount || 0, color: 'bg-success', textColor: 'text-success' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-16">{item.label}</span>
                      <div className="flex-1 h-6 rounded bg-muted/30 relative overflow-hidden">
                        <div
                          className={cn('h-full rounded flex items-center px-2', item.color)}
                          style={{
                            width: totalRisks ? `${Math.max((item.count / totalRisks) * 100, 15)}%` : '0%',
                          }}
                        >
                          <span className="text-[10px] text-card font-bold">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2: Severity Type + Likelihood Type */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">Severity Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={severityData} barSize={30}>
                    <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                    <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Bar dataKey="count" fill="oklch(0.35 0.12 145)" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 11, fill: 'oklch(0.48 0.03 145)' }} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">Likelihood Type</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={likelihoodData} barSize={30}>
                    <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                    <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Bar dataKey="count" fill="oklch(0.35 0.12 145)" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 11, fill: 'oklch(0.48 0.03 145)' }} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Risk Level Percentage Cards - Inspired by Reference */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card className="border border-border bg-muted/30">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-success">{lowPct}%</p>
                <div className="mt-2 h-2 w-full rounded-full bg-muted">
                  <div className="h-full rounded-full bg-success" style={{ width: `${lowPct}%` }} />
                </div>
                <p className="text-lg font-bold text-foreground mt-3">Low</p>
              </CardContent>
            </Card>
            <Card className="border border-border bg-muted/30">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-warning">{medPct}%</p>
                <div className="mt-2 h-2 w-full rounded-full bg-muted">
                  <div className="h-full rounded-full bg-warning" style={{ width: `${medPct}%` }} />
                </div>
                <p className="text-lg font-bold text-foreground mt-3">Medium</p>
              </CardContent>
            </Card>
            <Card className="border border-border bg-muted/30">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-destructive">{highPct}%</p>
                <div className="mt-2 h-2 w-full rounded-full bg-muted">
                  <div className="h-full rounded-full bg-destructive" style={{ width: `${highPct}%` }} />
                </div>
                <p className="text-lg font-bold text-foreground mt-3">High</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* REGISTER TAB */}
        <TabsContent value="register" className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search risks..." className="pl-10 h-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-9">
                <Filter className="mr-2 size-3.5" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="mitigated">Mitigated</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Description</TableHead>
                  <TableHead className="text-xs">Project</TableHead>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs text-center">L</TableHead>
                  <TableHead className="text-xs text-center">I</TableHead>
                  <TableHead className="text-xs text-center">Score</TableHead>
                  <TableHead className="text-xs">Owner</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                      Loading risks...
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                      No risks found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((risk) => {
                    const project = projectMap[risk.projectId]
                    const color = getRiskColor(risk.riskScore)
                  return (
                    <TableRow key={risk.id}>
                      <TableCell className="text-xs text-foreground max-w-[250px]">{risk.description}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {project?.code || project?.shortCode || project?.title || '-'}
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{risk.category}</Badge></TableCell>
                      <TableCell className="text-xs text-center text-foreground font-medium">{risk.likelihood}</TableCell>
                      <TableCell className="text-xs text-center text-foreground font-medium">{risk.impact}</TableCell>
                      <TableCell className="text-center"><Badge className={cn('text-[10px] font-bold', color.light)}>{risk.riskScore}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{risk.owner}</TableCell>
                      <TableCell>
                        <Badge className={cn('text-[10px] capitalize',
                          risk.status === 'open' ? 'bg-warning/15 text-warning' :
                          risk.status === 'mitigated' ? 'bg-success/15 text-success' :
                          'bg-muted text-muted-foreground'
                        )}>{risk.status}</Badge>
                      </TableCell>
                    </TableRow>
                  )
                }))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* MATRIX TAB */}
        <TabsContent value="matrix" className="mt-4">
          <Card className="border border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-foreground">Risk & Opportunity Matrix (Likelihood x Impact)</CardTitle>
              <CardDescription className="text-xs">Visual assessment of risk severity across the portfolio</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="min-w-[400px]">
                  <div className="grid grid-cols-6 gap-1">
                    <div className="flex items-center justify-center p-2">
                      <span className="text-[10px] text-muted-foreground font-medium">L / I</span>
                    </div>
                    {[1, 2, 3, 4, 5].map((impact) => (
                      <div key={impact} className="flex items-center justify-center p-2 bg-muted/50 rounded">
                        <span className="text-[10px] text-muted-foreground font-medium">I={impact}</span>
                      </div>
                    ))}
                    {[5, 4, 3, 2, 1].map((likelihood) => (
                      <React.Fragment key={likelihood}>
                        <div className="flex items-center justify-center p-2 bg-muted/50 rounded">
                          <span className="text-[10px] text-muted-foreground font-medium">L={likelihood}</span>
                        </div>
                        {[1, 2, 3, 4, 5].map((impact) => {
                          const score = likelihood * impact
                          const color = getRiskColor(score)
                          const risksInCell = normalizedRisks.filter((risk) => risk.likelihood === likelihood && risk.impact === impact)
                          return (
                            <div
                              key={`${likelihood}-${impact}`}
                              className={cn(
                                'flex flex-col items-center justify-center rounded-md p-2 min-h-[52px] transition-colors',
                                score >= 15 ? 'bg-destructive/20' : score >= 8 ? 'bg-warning/20' : score >= 4 ? 'bg-secondary/50' : 'bg-success/10'
                              )}
                            >
                              <span className="text-[10px] font-bold text-foreground">{score}</span>
                              {risksInCell.length > 0 && (
                                <Badge className="text-[8px] px-1 py-0 mt-0.5 bg-foreground/10 text-foreground">{risksInCell.length}</Badge>
                              )}
                            </div>
                          )
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
